<template>
    <div class="bg-white shadow overflow-hidden sm:rounded-md">
        <ul>
            <li class="border-t first:border-t-0 border-gray-200"
                v-for="(seat, index) in booking.seats"
            >
                <a href="#"
                   class="block hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition duration-150 ease-in-out"
                   @click.prevent="prepareEditSeat(seat)"
                >
                    <div class="px-4 py-4 sm:px-6">
                        <div class="flex items-center justify-between">
                            <div class="text-sm leading-5 font-medium text-primary-600 truncate">
                                Seat {{ index + 1 }}
                            </div>
                            <div class="ml-2 flex-shrink-0 flex">
                                <template v-if="seat.status === 0">
                                    <badge color="gray">Unassigned</badge>
                                </template>

                                <template v-else-if="seat.status === 1">
                                    <badge color="blue">Invite Sent</badge>
                                </template>

                                <template v-else>
                                    <badge>Accepted</badge>
                                </template>
                            </div>
                        </div>
                        <div class="mt-2 sm:flex sm:justify-between">
                            <div class="sm:flex">
                                <div class="mr-6 flex items-center text-sm leading-5 text-gray-500">
                                    <svg class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                        <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"></path>
                                    </svg>
                                    <template v-if="seat.status === 0">
                                        Unassigned&nbsp;
                                        <button type="button"
                                                class="text-primary-600 hover:underline focus:outline-none"
                                                @click.self.prevent="assignToSelf"
                                                v-if="index === 0"
                                        >
                                            Assign to self
                                        </button>
                                    </template>

                                    <template v-else-if="seat.status === 1">
                                        {{ seat.customer.email }}
                                    </template>

                                    <template v-else>
                                        {{ seat.customer.first_name }} {{ seat.customer.last_name }}
                                    </template>
                                </div>
                            </div>
                            <div class="mt-2 flex items-center text-sm leading-5 text-gray-500 sm:mt-0">
                                <svg class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                                <span>
                                    Add-ons: <span v-html="addOnsForSeat(seat)"></span>
                                </span>
                            </div>
                        </div>
                    </div>
                </a>
            </li>
        </ul>

        <portal to="modal">
            <modal v-if="editSeat"
                   @hide="editSeat = null;"
            >
                <template v-slot:default="props">
                    <h2>Edit Seat</h2>

                    <div class="sm:mt-6 sm:grid sm:grid-cols-1 sm:gap-3 sm:grid-flow-row-dense" v-if="editSeat">
                        <form-input v-model="editSeat.customer.email"
                                    type="email"
                                    label="Email Address"
                                    :read-only="editSeat.customer_id !== null"
                                    :error="getErrorFor('email')"
                                    @input="clearErrorFor('email');"
                        ></form-input>

                        <form-add-on-select v-model="editSeat.add_on_values[addOn.id]"
                                            :label="addOn.name"
                                            :options="addOn.selected_options"
                                            :assigned-quantities-on-load="addOnAssignedQuantities"
                                            :error="getErrorFor(`add_ons.${addOn.id}`)"
                                            v-for="(addOn, index) in individualAddOns"
                                            :key="addOn.id"
                                            :disabled="seatCount === 1"
                        ></form-add-on-select>
                    </div>

                    <div class="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                        <span class="flex w-full rounded-md shadow-sm sm:col-span-2"
                              v-if="editSeat && ! editSeat.member_lock && editSeat.status !== 0"
                        >
                            <form-button class="w-full"
                                         colour="red"
                                         @click.native="removeFromSeat(props.hide)"
                            >
                                Remove from Seat
                            </form-button>
                        </span>

                        <span class="flex w-full rounded-md shadow-sm sm:col-start-2">
                            <form-button class="w-full"
                                         @click.native="updateSeat(props.hide)"
                            >
                                Save
                            </form-button>
                        </span>
                        <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:col-start-1">
                            <form-button colour="white"
                                         class="w-full"
                                         @click.native="props.hide"
                            >
                                Cancel
                            </form-button>
                        </span>
                    </div>
                </template>
            </modal>

            <modal :wide="true"
                   v-if="getDetails"
                   @hide="getDetails = false;"
            >
                <template v-slot:default="props">
                    <div class="p-4 sm:p-6">
                        <h2 class="text-2xl mb-2">Your Details</h2>

                        <details-form :for-booking-owner="true"
                                      :form-data="customer"
                                      @details-updated="updateBookingOwnerSeat(props.hide)"
                        ></details-form>
                    </div>
                </template>
            </modal>
        </portal>
    </div>
</template>

<script>
    import FormInput from "../../../components/form/Input";
    import FormButton from "../../../components/form/Button";
    import FormAddOnSelect from "../../../components/form/AddOnSelect";
    import Badge from "../../../components/Badge";
    import Modal from "../../../components/Modal";
    import DetailsForm from './seats/invitations/Edit';

	export default {
	    data() {
	        return {
	            booking: null,
	            editSeat: null,
                errors: {},
                getDetails: null,
            }
        },

        props: {
            existingBooking: {
                type: Object,
                required: true,
            },

            addOns: {
                type: Array,
                required: true,
            },

            customer: {
                type: Object,
                required: true,
            }
        },

        components: { FormInput, FormButton, FormAddOnSelect, Badge, Modal, DetailsForm },

        created() {
	        this.booking = _.cloneDeep(this.existingBooking);
        },

        computed: {
	        individualAddOns() {
	            return _.reject(this.addOns, ['applies_to_group', true]);
            },

            addOnAssignedQuantities() {
	            let result = {};

	            _.each(this.booking.seats, seat => {
	                _.each(seat.add_ons, addOn => {
	                    if (! _.has(result, addOn.add_on_option_id)) {
                            result[addOn.add_on_option_id] = 0;
                        }

	                    result[addOn.add_on_option_id] += 1;
                    });
                });

	            return result;
            },

            seatCount() {
	            return this.booking.seats.length;
            },
        },

        methods: {
	        updateSeat(closeModal) {
	            if (this.editSeat.customer.email === this.customer.email && this.editSeat.status === 0) {
                    this.getDetails = {};

	                this.getDetails.seat_id = this.editSeat.id;
                    this.getDetails.add_on_values = this.editSeat.add_on_values;

                    closeModal();

	                return true;
                }

	            axios.patch(`/customer/bookings/seats`, {
	                booking_id: this.booking.id,
                    seat_id: this.editSeat.id,
                    email: this.editSeat.customer.email,
                    add_ons: this.editSeat.add_on_values,
                }).then(response => {
                    let seat = response.data;

                    this.booking.seats.splice(
                        _.findIndex(this.booking.seats, ['id', seat.id]),
                        1,
                        seat
                    );

                    closeModal();
                }).catch(error => {
                    this.errors = error.response.data.errors;
                });
            },

            updateBookingOwnerSeat(closeModal) {
                axios.patch(`/customer/bookings/seats`, {
                    booking_id: this.booking.id,
                    seat_id: this.getDetails.seat_id,
                    email: this.customer.email,
                    add_ons: this.getDetails.add_on_values,
                }).then(response => {
                    let seat = response.data;

                    this.booking.seats.splice(
                        _.findIndex(this.booking.seats, ['id', seat.id]),
                        1,
                        seat
                    );

                    closeModal();
                }).catch(error => {
                    this.errors = error.response.data.errors;
                });
            },

            removeFromSeat(closeModal) {
	            axios.delete(`/customer/bookings/seats/${this.editSeat.id}`)
                    .then(response => {
                        let seat = response.data;

                        this.booking.seats.splice(
                            _.findIndex(this.booking.seats, ['id', seat.id]),
                            1,
                            seat
                        );

                        closeModal();
                    }).catch(error => {
                        this.errors = error.response.data.errors;
                });
            },

            prepareEditSeat(seat) {
	            seat = _.cloneDeep(seat);

	            this.$set(seat, 'add_on_values', {});

                _.forEach(this.individualAddOns, addOn => {
                    if (this.seatCount > 1) {
                        let seatAddOn = _.find(seat.add_ons, ['add_on_id', addOn.id]);

                        this.$set(
                            seat.add_on_values,
                            addOn.id,
                            seatAddOn ? seatAddOn.add_on_option_id : null
                        );
                    } else {
                        this.$set(
                            seat.add_on_values,
                            addOn.id,
                            _.find(addOn.selected_options, addOn => { return addOn.name !== 'None' }).id
                        );
                    }
                });

	            this.editSeat = seat;
            },

            addOnsForSeat(seat) {
	            if (_.isEmpty(seat.add_ons)) return 'None';

	            let addOns = _.map(_.sortBy(seat.add_ons, addOn => { return addOn.add_on.name }), addOn => {
                    return `<span class="font-semibold">${addOn.add_on.name}</span>: ${addOn.add_on_option.name}`;
                });

	            return addOns.join(', ');
            },

            assignToSelf() {
	            setTimeout(() => {
                    this.$set(this.editSeat, 'customer_id', this.customer.id);
                    this.$set(this.editSeat, 'customer', this.customer);
                }, 50);
            },

            /**
             * Check if the given field has an error
             * @param field
             * @return {boolean}
             */
            hasError(field) {
                return _.has(this.errors, field);
            },

            /**
             * Get the error for the field
             * @param field
             */
            getErrorFor(field) {
                return this.hasError(field) ? this.errors[field][0] : '';
            },

            /**
             * Clear the errors for the given field
             * @param field
             */
            clearErrorFor(field) {
                this.$delete(this.errors, field);
            },
        }
    }
</script>
